import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { LayoutGrid } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { BankNotice } from '@/features/dashboard/bank-notice'
import { hojeIso, somarDias } from '@/features/dashboard/datas'
import {
  DIAS_MAX,
  DIAS_MIN,
  fimMaximo,
  HORIZONTES,
  horizonteAtivo,
  paramsDoRelatorio,
  resolverJanela,
} from '@/features/dashboard/horizonte'
import { montarDashboard } from '@/features/dashboard/series'
import { cashFlowQueryOptions } from '@/features/reports/api'
import { formatBusinessDate } from '@/features/schedules/components/date-text'
import { cn } from '@/shared/lib/cn'
import { formatMoney, fromCents, type Cents } from '@/shared/money/money'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { EmptyState } from '@/shared/ui/empty-state'
import { Input } from '@/shared/ui/input'
import { Skeleton } from '@/shared/ui/skeleton'
import { PageHeader } from '@/widgets/app-shell/page-header'
import { LineChart, type ChartSeries } from '@/widgets/chart/line-chart'

const searchSchema = z.object({
  // No search param (e não em estado local) para o horizonte sobreviver a
  // recarga e poder ser compartilhado por link. Default = 90 dias (3 meses):
  // ainda diário, e um trimestre projetado é o que faz sentido abrir de cara.
  dias: z.coerce.number().int().min(DIAS_MIN).max(DIAS_MAX).default(90),
  /**
   * Data final ABSOLUTA digitada à mão. Quando presente, vence o preset `dias`:
   * é o "não me prenda aos botões" do usuário. Fica no search para sobreviver a
   * recarga e a link; `resolverJanela` ignora um valor no passado e encolhe um
   * valor longe demais, então nenhuma data aqui derruba a tela.
   */
  ate: z.iso.date().optional(),
})

/** Rótulo de período: dia (YYYY-MM-DD) ou mês (YYYY-MM) — o backend emite os dois. */
function formatPeriodo(period: string, lang: string): string {
  if (period.length === 7) {
    const [ano, mes] = period.split('-').map(Number)
    return new Intl.DateTimeFormat(lang, { month: 'short', year: 'numeric' }).format(
      new Date(ano ?? 1970, (mes ?? 1) - 1, 1),
    )
  }
  return formatBusinessDate(period, lang)
}

/**
 * Base sem NENHUM movimento — nem saldo, nem agendamento, nem histórico, nem
 * banco.
 *
 * Distinguir isto de "carregando" importa: um gráfico de linhas achatado no zero
 * parece um bug. Aqui a tela diz que ainda não há o que mostrar.
 *
 * A origem BANCÁRIA entra na conta de propósito: sem ela, quem só tem
 * integração (zero agendamento) leria "ainda não há movimento" com o banco
 * cheio de contas — exatamente a tela vazia que motivou esta mudança.
 */
function tudoZerado(d: ReturnType<typeof montarDashboard>): boolean {
  return (
    d.resumo.saldoHoje === 0 &&
    d.resumo.aReceber === 0 &&
    d.resumo.aPagar === 0 &&
    d.entradas.every((p) => p.y === 0) &&
    d.saidas.every((p) => p.y === 0) &&
    d.entradasBanco.every((p) => p.y === 0) &&
    d.saidasBanco.every((p) => p.y === 0)
  )
}

export const Route = createFileRoute('/_authed/inicio')({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ dias: search.dias, ate: search.ate }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(
      cashFlowQueryOptions(paramsDoRelatorio(resolverJanela(deps, hojeIso()))),
    ),
  component: InicioPage,
})

function InicioPage() {
  const { t, i18n } = useTranslation(['dashboard', 'nav', 'common'])
  const { dias, ate } = Route.useSearch()
  const navigate = Route.useNavigate()

  // hojeIso() é estável dentro do dia; recomputar a janela só quando o search
  // muda mantém a queryKey estável entre loader e componente (mesmo GET).
  const janela = useMemo(() => resolverJanela({ dias, ate }, hojeIso()), [dias, ate])
  const { hoje, to: fimJanela, groupBy } = janela
  const { data, isLoading } = useQuery(cashFlowQueryOptions(paramsDoRelatorio(janela)))
  // No modo mensal, o eixo X usa rótulos 'YYYY-MM' — a marca de "hoje" precisa
  // apontar para o MÊS corrente para o indexOf do gráfico achar.
  const marcaHoje = groupBy === 'day' ? hoje : hoje.slice(0, 7)

  const dados = useMemo(() => (data ? montarDashboard(data, hoje) : undefined), [data, hoje])

  const dinheiro = (cents: number) => formatMoney(fromCents(cents as Cents), i18n.language)

  /**
   * Eixo Y em notação compacta ("R$ 15 mil"): o rótulo completo ocuparia metade
   * da largura do gráfico. É o ÚNICO lugar onde o valor vira float — rótulo de
   * magnitude, nunca aritmética (a conta continua toda em centavos inteiros).
   */
  const dinheiroCompacto = (cents: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'BRL',
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(cents / 100)

  /**
   * Quatro séries, nunca somadas: contábil e banco são ORIGENS diferentes e a
   * mesma dívida pode estar nas duas (duplicidade aceita pelo produto até o
   * impasse ser resolvido). Somar produziria um número que não existe em lugar
   * nenhum; separadas, cada linha é verdadeira sozinha.
   *
   * Série bancária VAZIA não vira linha zerada — ela simplesmente não entra.
   */
  const series: readonly ChartSeries[] = useMemo(() => {
    if (!dados) return []
    const base: ChartSeries[] = [
      {
        id: 'inflow',
        label: t('dashboard:legend.inflow'),
        color: 'var(--color-chart-inflow)',
        points: dados.entradas,
      },
      {
        id: 'outflow',
        label: t('dashboard:legend.outflow'),
        color: 'var(--color-chart-outflow)',
        points: dados.saidas,
      },
    ]
    if (dados.entradasBanco.length > 0) {
      base.push({
        id: 'bank-inflow',
        label: t('dashboard:legend.bankInflow'),
        color: 'var(--color-chart-5)',
        points: dados.entradasBanco,
      })
    }
    if (dados.saidasBanco.length > 0) {
      base.push({
        id: 'bank-outflow',
        label: t('dashboard:legend.bankOutflow'),
        color: 'var(--color-chart-6)',
        points: dados.saidasBanco,
      })
    }
    return base
  }, [dados, t])

  const semMovimento = dados !== undefined && tudoZerado(dados)

  return (
    <>
      <PageHeader
        title={t('nav:home')}
        actions={
          <ControleHorizonte
            dias={dias}
            ate={ate}
            fim={fimJanela}
            onPreset={(n) => {
              // Objeto (não updater funcional): substitui o search inteiro, então
              // `ate: undefined` LIMPA a data manual — clicar num preset faz o
              // botão voltar a ser a verdade. Ambos os campos explícitos deixam a
              // intenção clara e o `prev` fora de cena (o updater funcional cai
              // para `any` nesta versão do router).
              void navigate({ search: { dias: n, ate: undefined }, replace: true })
            }}
            onData={(d) => {
              // Mantém o preset atual em `dias` (fica de reserva se a data for
              // limpa) e grava/limpa a data manual em `ate`.
              void navigate({ search: { dias, ate: d }, replace: true })
            }}
          />
        }
      />

      <div className="flex flex-col gap-4 p-6">
        {isLoading || !dados ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full rounded-lg" />
              ))}
            </div>
            <Skeleton className="h-80 w-full rounded-lg" />
          </>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Tile
                titulo={t('dashboard:tiles.balanceToday')}
                valor={dinheiro(dados.resumo.saldoHoje)}
                dica={t('dashboard:tiles.balanceTodayHint')}
              />
              <Tile
                titulo={t('dashboard:tiles.receivable')}
                valor={dinheiro(dados.resumo.aReceber)}
                dica={t('dashboard:tiles.scheduledHint')}
                tom="income"
              />
              <Tile
                titulo={t('dashboard:tiles.payable')}
                valor={dinheiro(dados.resumo.aPagar)}
                dica={t('dashboard:tiles.scheduledHint')}
                tom="expense"
              />
              <Tile
                titulo={t('dashboard:tiles.projectedBalance')}
                valor={dinheiro(dados.resumo.saldoProjetado)}
                dica={t('dashboard:tiles.projectedHint')}
                // Saldo projetado NEGATIVO é o alerta mais importante da tela.
                tom={dados.resumo.saldoProjetado < 0 ? 'expense' : 'neutro'}
                destaque
              />
            </div>

            <BankNotice banco={dados.banco} dinheiro={dinheiro} />

            <Card>
              <CardHeader>
                <CardTitle>{t('dashboard:chart.title')}</CardTitle>
                <Legenda series={series} nota={t('dashboard:legend.projected')} />
              </CardHeader>
              <CardContent>
                {semMovimento ? (
                  <EmptyState
                    icon={LayoutGrid}
                    title={t('dashboard:empty.title')}
                    description={t('dashboard:empty.description')}
                  />
                ) : (
                  <>
                    <LineChart
                      series={series}
                      ariaLabel={t('dashboard:chart.aria')}
                      formatValue={dinheiroCompacto}
                      formatDate={(iso) => formatPeriodo(iso, i18n.language)}
                      todayX={marcaHoje}
                      todayLabel={t('dashboard:chart.today')}
                    />
                    {/* Exigência de acessibilidade: o gráfico nunca é a ÚNICA via
                        para o dado. Quem usa leitor de tela (ou quer o número
                        exato) abre a tabela. */}
                    <TabelaDados dados={dados} />
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </>
  )
}

/**
 * Seletor de horizonte: controle segmentado (estilo iOS) de presets MAIS um
 * campo de data para o fim exato. O preset é o caminho de um clique; a data à
 * mão é a válvula de escape para quem quer um fim que nenhum botão dá — sem
 * prender o usuário à grade de opções.
 *
 * Com uma data manual ativa, NENHUM preset fica aceso (a data é a verdade). A
 * legenda embaixo mostra a data concreta em que a projeção termina — que, para
 * horizontes longos, é o fim REAL já encolhido ao teto do backend, então o
 * rótulo nunca promete mais do que o gráfico entrega.
 */
function ControleHorizonte({
  dias,
  ate,
  fim,
  onPreset,
  onData,
}: {
  dias: number
  ate: string | undefined
  /** Fim EFETIVO da janela (já ancorado e encolhido) — o que a legenda mostra. */
  fim: string
  onPreset: (dias: number) => void
  onData: (ate: string | undefined) => void
}) {
  const { t, i18n } = useTranslation(['dashboard'])
  const hoje = hojeIso()
  // Data manual só "vale" se for futura; passado/hoje resolverJanela ignora, e o
  // controle acompanha (volta a acender o preset).
  const manual = ate !== undefined && ate > hoje
  const ativo = horizonteAtivo(dias)
  const fimTexto = formatBusinessDate(fim, i18n.language)

  return (
    <div className="flex flex-col items-start gap-1.5 sm:items-end">
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        <div
          role="group"
          aria-label={t('dashboard:horizon.label')}
          className="border-line bg-surface-2 inline-flex flex-wrap gap-0.5 rounded-lg border p-0.5"
        >
          {HORIZONTES.map((h) => {
            const selecionado = !manual && h.dias === ativo
            return (
              <button
                key={h.chave}
                type="button"
                aria-pressed={selecionado}
                onClick={() => {
                  onPreset(h.dias)
                }}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  selecionado ? 'bg-action text-white shadow-sm' : 'text-ink-muted hover:text-ink',
                )}
              >
                {t(`dashboard:horizon.presets.${h.chave}`)}
              </button>
            )
          })}
        </div>
        <label className="text-ink-muted flex items-center gap-1.5 text-xs">
          <span>{t('dashboard:horizon.orDate')}</span>
          <Input
            type="date"
            aria-label={t('dashboard:horizon.customDate')}
            className="h-7 w-auto"
            // min amanhã / max o teto do backend: o seletor barra por construção
            // a faixa que resolverJanela precisaria ignorar ou encolher.
            min={somarDias(hoje, 1)}
            max={fimMaximo(hoje)}
            value={manual ? ate : ''}
            onChange={(e) => {
              onData(e.target.value === '' ? undefined : e.target.value)
            }}
          />
        </label>
      </div>
      <span className="text-ink-subtle text-2xs">
        {t('dashboard:horizon.through', { data: fimTexto })}
      </span>
    </div>
  )
}

function Tile({
  titulo,
  valor,
  dica,
  tom = 'neutro',
  destaque = false,
}: {
  titulo: string
  valor: string
  dica: string
  tom?: 'neutro' | 'income' | 'expense'
  destaque?: boolean
}) {
  return (
    <Card className={cn(destaque && 'border-line-strong')}>
      <CardContent className="flex flex-col gap-1 pt-4">
        <p className="text-ink-muted text-2xs font-medium tracking-wide uppercase">{titulo}</p>
        <p
          className={cn(
            'tnum text-2xl font-semibold',
            tom === 'income' && 'text-income',
            tom === 'expense' && 'text-expense',
            tom === 'neutro' && 'text-ink',
          )}
        >
          {valor}
        </p>
        <p className="text-ink-subtle text-2xs">{dica}</p>
      </CardContent>
    </Card>
  )
}

/** Legenda sempre presente: com 2 séries, identidade nunca pode ser só a cor. */
function Legenda({ series, nota }: { series: readonly ChartSeries[]; nota: string }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      {series.map((s) => (
        <span key={s.id} className="text-ink-muted flex items-center gap-1.5 text-sm">
          <span
            aria-hidden
            className="h-0.5 w-4 shrink-0 rounded-full"
            style={{ backgroundColor: s.color }}
          />
          {s.label}
        </span>
      ))}
      <span className="text-ink-subtle text-2xs">{nota}</span>
    </div>
  )
}

function TabelaDados({ dados }: { dados: ReturnType<typeof montarDashboard> }) {
  const { t, i18n } = useTranslation(['dashboard'])
  // Valor bancário AUSENTE vira '—', nunca 0: a tabela é a via alternativa ao
  // gráfico e não pode afirmar o que o gráfico se recusou a plotar.
  const bancoEntrada = new Map(dados.entradasBanco.map((p) => [p.x, p.y]))
  const bancoSaida = new Map(dados.saidasBanco.map((p) => [p.x, p.y]))
  const temBanco = bancoEntrada.size > 0 || bancoSaida.size > 0

  const linhas = dados.entradas.map((e, i) => ({
    data: e.x,
    entrada: e.y,
    saida: dados.saidas[i]?.y ?? 0,
    bancoEntrada: bancoEntrada.get(e.x),
    bancoSaida: bancoSaida.get(e.x),
    projetado: e.projected,
  }))
  const dinheiro = (c: number) => formatMoney(fromCents(c as Cents), i18n.language)
  const dinheiroOuTraco = (c: number | undefined) => (c === undefined ? '—' : dinheiro(c))

  return (
    <details className="mt-4">
      <summary className="text-action cursor-pointer text-sm hover:underline">
        {t('dashboard:table.summary')}
      </summary>
      <div className="mt-2 max-h-80 overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-ink-muted text-2xs sticky top-0 text-left uppercase">
            <tr className="bg-surface">
              <th className="py-1 pr-3 font-medium">{t('dashboard:table.date')}</th>
              <th className="py-1 pr-3 text-right font-medium">{t('dashboard:table.inflow')}</th>
              <th className="py-1 pr-3 text-right font-medium">{t('dashboard:table.outflow')}</th>
              {temBanco && (
                <>
                  <th className="py-1 pr-3 text-right font-medium">
                    {t('dashboard:table.bankInflow')}
                  </th>
                  <th className="py-1 pr-3 text-right font-medium">
                    {t('dashboard:table.bankOutflow')}
                  </th>
                </>
              )}
              <th className="py-1 font-medium">{t('dashboard:table.kind')}</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.data} className="border-line border-t">
                <td className="py-1 pr-3">{formatPeriodo(l.data, i18n.language)}</td>
                <td className="tnum py-1 pr-3 text-right">{dinheiro(l.entrada)}</td>
                <td className="tnum py-1 pr-3 text-right">{dinheiro(l.saida)}</td>
                {temBanco && (
                  <>
                    <td className="tnum py-1 pr-3 text-right">{dinheiroOuTraco(l.bancoEntrada)}</td>
                    <td className="tnum py-1 pr-3 text-right">{dinheiroOuTraco(l.bancoSaida)}</td>
                  </>
                )}
                <td className="text-ink-muted py-1">
                  {t(l.projetado ? 'dashboard:table.projected' : 'dashboard:table.realized')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}
